import React, { FC } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import {
  AssistMigrationMachineInfo,
  AssistMigrationMachineSizePricing,
} from 'types/assist/assistMigration';
import { FaqEditor } from './FaqEditor';

interface MachineInfoEditorProps {
  label: string;
  value: AssistMigrationMachineInfo | null;
  onChange: (next: AssistMigrationMachineInfo | null) => void;
}

const EMPTY_MACHINE_INFO: AssistMigrationMachineInfo = {
  message: '',
  faqs: [],
  sizesAndPricing: [],
};

export const MachineInfoEditor: FC<MachineInfoEditorProps> = ({ label, value, onChange }) => {
  const setSize = (index: number, patch: Partial<AssistMigrationMachineSizePricing>) => {
    if (!value) return;

    onChange({
      ...value,
      sizesAndPricing: value.sizesAndPricing.map((item, itemIndex) =>
        itemIndex === index ? { ...item, ...patch } : item,
      ),
    });
  };

  if (!value) {
    return (
      <div style={{ marginBottom: 12 }}>
        <Typography variant="caption" style={{ fontWeight: 600, display: 'block' }}>
          {label}
        </Typography>
        <Button size="small" onClick={() => onChange(EMPTY_MACHINE_INFO)}>
          + {label.toLowerCase()} info
        </Button>
      </div>
    );
  }

  return (
    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1, mb: 1.5 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" style={{ fontWeight: 600 }}>
          {label}
        </Typography>
        <Button size="small" color="error" onClick={() => onChange(null)}>
          Clear
        </Button>
      </div>
      <TextField
        size="small"
        label={`${label} guidance for agent`}
        value={value.message ?? ''}
        fullWidth
        multiline
        helperText={`Caller-facing facts about ${label.toLowerCase()}; do not repeat sizes or prices.`}
        style={{ marginBottom: 8 }}
        onChange={(event) => onChange({ ...value, message: event.target.value || null })}
      />
      {value.sizesAndPricing.map((item, index) => (
        <div key={index} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
          <TextField
            size="small"
            label="Size"
            value={item.size}
            fullWidth
            onChange={(event) => setSize(index, { size: event.target.value })}
          />
          <TextField
            size="small"
            label="Min price"
            type="number"
            style={{ width: 144 }}
            value={item.minPrice}
            onChange={(event) =>
              setSize(index, {
                minPrice: event.target.value === '' ? -1 : Number(event.target.value),
              })
            }
          />
          <TextField
            size="small"
            label="Max price"
            type="number"
            style={{ width: 144 }}
            value={item.maxPrice ?? ''}
            onChange={(event) =>
              setSize(index, {
                maxPrice: event.target.value === '' ? null : Number(event.target.value),
              })
            }
          />
          <Button
            size="small"
            color="error"
            onClick={() =>
              onChange({
                ...value,
                sizesAndPricing: value.sizesAndPricing.filter(
                  (_, itemIndex) => itemIndex !== index,
                ),
              })
            }
          >
            ×
          </Button>
        </div>
      ))}
      <Button
        size="small"
        onClick={() =>
          onChange({
            ...value,
            sizesAndPricing: [...value.sizesAndPricing, { size: '', minPrice: -1, maxPrice: null }],
          })
        }
      >
        + size &amp; price
      </Button>
      <Typography variant="caption" style={{ fontWeight: 600, display: 'block', marginTop: 8 }}>
        {label} FAQs
      </Typography>
      <FaqEditor items={value.faqs} onChange={(faqs) => onChange({ ...value, faqs })} />
    </Box>
  );
};
